'use client';

import { useSession, signIn, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

const AUTH_LAST_URL_KEY = 'auth:last-url';
const AUTH_REDIRECT_FLAG = 'auth:redirect-pending';
const AUTH_POPUP_NAME = 'inovisum-auth';
const AUTH_CHANNEL = 'inovisum-auth';

export interface AuthUserSummary {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  isSuperAdmin?: boolean;
}

export interface AuthControls {
  status: 'loading' | 'authenticated' | 'unauthenticated';
  user: AuthUserSummary | null;
  isSigningOut: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const rememberAuthIntent = () => {
  try {
    const { pathname, search } = window.location;
    if (!pathname.startsWith('/auth')) {
      const relativeUrl = `${pathname}${search || ''}`;
      window.sessionStorage?.setItem(AUTH_LAST_URL_KEY, relativeUrl);
    }
    window.sessionStorage?.setItem(AUTH_REDIRECT_FLAG, '1');
  } catch {
    // ignore storage errors
  }
};

export function useAuthControls(provider?: string): AuthControls {
  const { data: session, status, update } = useSession();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const router = useRouter();

  const popupRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const cleanupMessageListener = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
  }, []);

  const closePopup = useCallback(() => {
    cleanupMessageListener();
    try {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    } catch {
      // ignore
    }
    popupRef.current = null;
  }, [cleanupMessageListener]);

  // Clean up message listener on unmount
  useEffect(() => cleanupMessageListener, [cleanupMessageListener]);

  // Channel for popup coordination
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channelRef.current = channel;

    const handleChannelMessage = (event: MessageEvent) => {
      try {
        const payload = event.data;
        if (!payload || typeof payload !== 'object') return;
        if (payload.type !== 'auth:close-popup') return;

        closePopup();
      } catch (error) {
        if (error instanceof Error && error.message.includes('message channel closed')) {
          return;
        }
        console.warn('Auth popup broadcast handling error:', error);
      }
    };

    channel.addEventListener('message', handleChannelMessage);

    return () => {
      channel.removeEventListener('message', handleChannelMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [closePopup]);

  const handleSignIn = useCallback(async () => {
    rememberAuthIntent();

    if (popupRef.current && !popupRef.current.closed) {
      try {
        popupRef.current.focus();
        return;
      } catch {
        // fall through to opening a new popup
      }
    }

    closePopup();
    try {
      // Always return through the dedicated completion page so the popup
      // can notify the opener and close itself.
      const callbackUrl = '/auth/complete';

      // Compute window size/position (taller popup for SSO providers)
      const ratio = 6 / 5;
      const minWidth = 280;
      const maxWidth = 400;
      const minHeight = Math.round(minWidth * ratio);
      const maxHeight = Math.round(maxWidth * ratio);
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || window.screen.width || 1024;
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || window.screen.height || 768;
      let w = Math.min(maxWidth, Math.max(minWidth, viewportWidth * 0.85));
      let h = Math.round(w * ratio);

      if (h > viewportHeight * 0.9) {
        h = Math.min(maxHeight, Math.max(minHeight, viewportHeight * 0.9));
        w = Math.round(Math.max(minWidth, Math.min(maxWidth, h / ratio)));
        h = Math.round(Math.max(minHeight, Math.min(maxHeight, w * ratio)));
      } else {
        h = Math.round(Math.max(minHeight, Math.min(maxHeight, h)));
      }

      const dualScreenLeft = (window.screenLeft ?? window.screenX) || 0;
      const dualScreenTop = (window.screenTop ?? window.screenY) || 0;
      const vw =
        window.outerWidth || window.innerWidth || document.documentElement.clientWidth || window.screen.width || w;
      const vh =
        window.outerHeight || window.innerHeight || document.documentElement.clientHeight || window.screen.height || h;
      const left = dualScreenLeft + Math.max(0, (vw - w) / 2);
      const top = dualScreenTop + Math.max(0, (vh - h) / 2);

      const features = `width=${w},height=${h},left=${left},top=${top},screenX=${left},screenY=${top},toolbar=0,location=0,status=0,menubar=0,scrollbars=1,resizable=1`;
      const providerPath = provider ? `/${provider}` : '';

      const popup = window.open(
        `/api/auth/signin${providerPath}?callbackUrl=${encodeURIComponent(callbackUrl)}`,
        AUTH_POPUP_NAME,
        features
      );

      if (popup) {
        popupRef.current = popup;
        try {
          popup.resizeTo(w, h);
          popup.moveTo(left, top);
          popup.focus();
        } catch {
          // ignore move/resize failures
        }

        const enforceCenter = () => {
          try {
            if (popup.closed) return;
            popup.resizeTo(w, h);
            popup.moveTo(left, top);
          } catch {
            // ignore
          }
        };
        setTimeout(enforceCenter, 50);

        const handleMessage = (event: MessageEvent) => {
          try {
            if (event.origin !== window.location.origin) return;

            if ((event.data as any)?.type === 'NEXTAUTH_SIGNIN_SUCCESS') {
              setTimeout(() => {
                closePopup();
                channelRef.current?.postMessage({ type: 'auth:close-popup' });
                update().catch((error) => {
                  console.warn('Failed to refresh session after popup sign-in', error);
                });
              }, 0);
            }
          } catch (error) {
            if (error instanceof Error && error.message.includes('message channel closed')) {
              return;
            }
            console.warn('Popup message handling error:', error);
          }
        };

        messageHandlerRef.current = handleMessage;
        window.addEventListener('message', handleMessage);
      } else {
        // Fallback to regular redirect if popup blocked
        await signIn(provider ?? undefined, { callbackUrl });
      }
    } catch (error) {
      console.error('Sign in error:', error);
    }
  }, [closePopup, provider, update]);

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    try {
      // Sign out without redirecting. Stay on the current page and refresh
      // so the server can re-evaluate access - if the user no longer has access,
      // they'll see the appropriate error/login page.
      await signOut({ redirect: false });
      router.refresh();
    } finally {
      setIsSigningOut(false);
    }
  }, [router]);

  const user: AuthUserSummary | null =
    status === 'authenticated' && session?.user?.id
      ? {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          image: (session.user.image as string | null | undefined) ?? null,
          isSuperAdmin: session.user.isSuperAdmin ?? false,
        }
      : null;

  return {
    status,
    user,
    isSigningOut,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };
}
